import { useRouterState } from "@tanstack/react-router";
import { Menu, Pause, Play, X } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { Link } from "@/components/link";
import { usePlayer } from "@/components/providers/player-provider";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { specialPages } from "@/lib/consts";
import { getPublicNowPlaying } from "@/lib/explore";
import type { Page } from "@/lib/types";
import { capitalizeEveryWord, cn } from "@/lib/utils";

import { Logo } from "./logo";
import { Button } from "./ui/button";

const heroLinkStyle =
  "bg-transparent text-primary-foreground/85 hover:bg-white/15 hover:text-primary-foreground focus:bg-white/15 focus:text-primary-foreground data-[state=open]:bg-white/15 data-[state=open]:text-primary-foreground";

const defaultItems = [
  {
    href: "/",
    text: "Home",
  },
  {
    href: "/explore",
    text: "Explore",
  },
];

function LivePlayControl({
  live,
  variant,
}: {
  live: boolean;
  variant: "default" | "hero";
}) {
  const { current, playLive, playing, toggle } = usePlayer();
  const livePlaying = current?.kind === "live" && playing;
  const playbackLabel = livePlaying ? "Pause live radio" : "Listen live";

  return (
    <button
      aria-label={live ? playbackLabel : "Off air"}
      disabled={!live}
      className={cn(
        "inline-flex h-10 shrink-0 items-center gap-2 rounded-full whitespace-nowrap py-1.5 pe-4 ps-2 text-xs font-semibold transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60",
        !live && "ps-4",
        variant === "hero"
          ? "bg-white text-foreground hover:bg-white/90"
          : "bg-muted text-foreground hover:bg-muted/80"
      )}
      onClick={() => {
        if (current?.kind === "live") {
          toggle();
        } else {
          playLive();
        }
      }}
      type="button"
    >
      {live && (
        <span className="bg-primary text-primary-foreground inline-flex size-6 items-center justify-center rounded-full">
          {livePlaying ? (
            <Pause className="size-3 fill-current" />
          ) : (
            <Play className="size-3 translate-x-px fill-current" />
          )}
        </span>
      )}
      <span
        className={cn(
          "size-2 rounded-full",
          live ? "bg-primary" : "bg-muted-foreground/40"
        )}
      />
      {live ? "Live now" : "Off air"}
    </button>
  );
}

function MobileLink({
  children,
  className,
  href,
  onOpenChange,
}: {
  children: React.ReactNode;
  className?: string;
  href: string;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Link
      className={cn(
        "-mx-4 rounded-2xl px-4 py-2 text-2xl font-semibold hover:bg-primary/10 hover:text-primary",
        className
      )}
      href={href}
      onClick={() => onOpenChange(false)}
    >
      {children}
    </Link>
  );
}

const ListItem = React.forwardRef<
  React.ElementRef<typeof Link>,
  React.ComponentPropsWithoutRef<typeof Link>
>(({ className, title, children, ...props }, ref) => (
  <li>
    <NavigationMenuLink
      render={
        <Link
          ref={ref}
          className={cn(
            "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
            className
          )}
          {...props}
        >
          <div className="text-sm font-medium leading-none">
            {capitalizeEveryWord(title ?? "")}
          </div>
          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
            {children}
          </p>
        </Link>
      }
    />
  </li>
));
ListItem.displayName = "ListItem";

export function NavBar({
  className,
  pages,
  variant = "default",
  ...props
}: React.HTMLAttributes<HTMLElement> & {
  pages: Page[];
  variant?: "default" | "hero";
}) {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  const decodedPathname = useMemo(() => {
    try {
      return decodeURIComponent(pathname);
    } catch {
      return pathname;
    }
  }, [pathname]);

  const [burgerOpened, setBurgerOpened] = useState(false);
  const [live, setLive] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    let active = true;

    const check = async () => {
      try {
        const episode = await getPublicNowPlaying();
        if (active) {
          setLive(episode !== null);
        }
      } catch {
        setLive(false);
      }
    };

    check();

    const id = setInterval(check, 30_000);

    return () => {
      active = false;
      clearInterval(id);
    };
  }, []);

  const groupedPages = useMemo(() => {
    const groupedObject: Record<string, Page[]> = {};

    for (const page of pages) {
      if (page.category === null) {
        continue;
      }
      if (!Object.hasOwn(groupedObject, page.category)) {
        groupedObject[page.category] = [];
      }
      groupedObject[page.category].push(page);
    }

    return groupedObject;
  }, [pages]);

  const uncategorizedPages = pages
    .filter((it) => it.category === null)
    .filter((it) => !specialPages.includes(it.name));

  return (
    <div>
      <Popover onOpenChange={setBurgerOpened} open={burgerOpened}>
        <nav
          ref={navRef}
          className={cn(
            "w-full",
            variant === "hero"
              ? "text-primary-foreground"
              : "sticky top-0 z-40 border-b bg-white shadow-md shadow-black/5",
            className
          )}
          {...props}
        >
          <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
            <div className="flex items-center gap-10">
              <Logo nameClassName="max-sm:sr-only" />
              <NavigationMenu className="hidden lg:block **:data-open:animate-none **:data-closed:animate-none **:data-starting-style:opacity-100 **:data-ending-style:opacity-100 **:data-starting-style:transform-none **:data-ending-style:transform-none">
                <NavigationMenuList>
                  {defaultItems.map((it, i) => (
                    <NavigationMenuItem
                      key={i}
                      className={cn(
                        decodedPathname === it.href &&
                          (variant === "hero"
                            ? "[&_a]:bg-white [&_a]:text-primary [&_a]:hover:bg-white [&_a]:hover:text-black"
                            : "text-red-600")
                      )}
                    >
                      <NavigationMenuLink
                        render={
                          <Link
                            href={it.href}
                            className={cn(
                              navigationMenuTriggerStyle(),
                              variant === "hero" && heroLinkStyle
                            )}
                          >
                            {capitalizeEveryWord(it.text)}
                          </Link>
                        }
                      />
                    </NavigationMenuItem>
                  ))}
                  {uncategorizedPages.map((it) => (
                    <NavigationMenuItem
                      key={it.id}
                      className={cn(
                        decodedPathname === `/${it.name}` && "text-red-600"
                      )}
                    >
                      <NavigationMenuLink
                        render={
                          <Link
                            href={`/${encodeURIComponent(it.name)}`}
                            className={cn(
                              navigationMenuTriggerStyle(),
                              variant === "hero" && heroLinkStyle
                            )}
                          >
                            {capitalizeEveryWord(it.name)}
                          </Link>
                        }
                      />
                    </NavigationMenuItem>
                  ))}
                  {Object.keys(groupedPages).map((categoryName) => (
                    <NavigationMenuItem key={categoryName}>
                      <NavigationMenuTrigger
                        className={cn(
                          groupedPages[categoryName].some(
                            (page) =>
                              decodedPathname ===
                              `/${categoryName}/${page.name}`
                          ) && "text-red-600",
                          variant === "hero" && heroLinkStyle
                        )}
                      >
                        {capitalizeEveryWord(categoryName)}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                          {groupedPages[categoryName].map((component) => (
                            <ListItem
                              key={component.name}
                              title={component.name}
                              href={`/${encodeURIComponent(
                                categoryName
                              )}/${encodeURIComponent(component.name)}`}
                            />
                          ))}
                        </ul>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  ))}
                </NavigationMenuList>
              </NavigationMenu>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <LivePlayControl live={live} variant={variant} />
              <PopoverTrigger
                render={
                  <Button
                    className={cn(
                      "lg:hidden",
                      variant === "hero" &&
                        "bg-white/15 text-primary-foreground hover:bg-white/25"
                    )}
                  >
                    {burgerOpened ? (
                      <X className="h-8 w-8" />
                    ) : (
                      <Menu className="h-8 w-8" />
                    )}
                    <span className="sr-only">Toggle menu</span>
                  </Button>
                }
              />
            </div>
          </div>
        </nav>

        <PopoverContent
          align="start"
          alignOffset={0}
          anchor={navRef}
          className="h-(--available-height) w-(--available-width) overflow-y-auto rounded-none border-none bg-background p-0 shadow-none ring-0 transition-none data-open:animate-none! data-closed:animate-none!"
          side="bottom"
          sideOffset={0}
        >
          <div className="flex flex-col gap-10 px-6 py-6">
            <div className="flex flex-col gap-4">
              <div className="text-sm font-medium text-muted-foreground">
                Menu
              </div>
              <div className="flex flex-col gap-3">
                {defaultItems.map((it) => (
                  <MobileLink
                    className={cn(
                      decodedPathname === it.href && "text-primary"
                    )}
                    href={it.href}
                    key={it.href}
                    onOpenChange={setBurgerOpened}
                  >
                    {capitalizeEveryWord(it.text)}
                  </MobileLink>
                ))}
              </div>
            </div>

            {uncategorizedPages.length > 0 && (
              <div className="flex flex-col gap-4">
                <div className="text-sm font-medium text-muted-foreground">
                  Pages
                </div>
                <div className="flex flex-col gap-3">
                  {uncategorizedPages.map((it) => (
                    <MobileLink
                      className={cn(
                        decodedPathname === `/${it.name}` && "text-primary"
                      )}
                      href={`/${encodeURIComponent(it.name)}`}
                      key={it.id}
                      onOpenChange={setBurgerOpened}
                    >
                      {capitalizeEveryWord(it.name)}
                    </MobileLink>
                  ))}
                </div>
              </div>
            )}

            {Object.keys(groupedPages).map((categoryName) => (
              <div className="flex flex-col gap-4" key={categoryName}>
                <div className="text-sm font-medium text-muted-foreground">
                  {capitalizeEveryWord(categoryName)}
                </div>
                <div className="flex flex-col gap-3">
                  {groupedPages[categoryName].map((it) => (
                    <MobileLink
                      className={cn(
                        decodedPathname === `/${categoryName}/${it.name}` &&
                          "text-primary"
                      )}
                      href={`/${encodeURIComponent(
                        categoryName
                      )}/${encodeURIComponent(it.name)}`}
                      key={it.id}
                      onOpenChange={setBurgerOpened}
                    >
                      {capitalizeEveryWord(it.name)}
                    </MobileLink>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
